import { Icon } from "@/components/Icons";
import { Button, Modal } from "@arco-design/web-react";
import React, { useCallback, useContext, useMemo } from "react";
import "./index.scss";
import { useShowcaseAuthUrl } from "@/api/product/use-query";
import { clientWindow } from "@/utils/dom";
import { onErrorMessage } from "@/utils/message";
import { TrackingBtnId, TrackingEventID, TrackingEventType } from "@/shared";
import { useTrackingRecordSave } from "@/api/track/use-query";
import { LoginContext } from "@/app/LoginProvider";
import { EventConfig } from "@/hooks/useAddShowcase";
import { TProductInfo } from "@/typings";
import { usePathname, useSearchParams } from "next/navigation";
import { base64Encode } from "@/utils/url";

interface ShowcaseAuthModalProps {
  visible: boolean;
  onCancel: () => void;
  eventConfig?: EventConfig;
  product: TProductInfo;
}

const ShowcaseAuthModal = (props: ShowcaseAuthModalProps) => {
  const { visible, onCancel, eventConfig, product } = props;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchParamsStr = searchParams.toString();

  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  // 橱窗授权
  const { mutate: fetchShowcaseAuthUrl, isLoading: isShowcaseAuthUrlLoading } =
    useShowcaseAuthUrl();

  const loginCtx = useContext(LoginContext);

  const callback = useMemo(() => {
    return searchParamsStr ? `${pathname}?${searchParamsStr}` : pathname;
  }, [searchParamsStr, pathname]);

  const goAuth = useCallback(() => {
    if (callback) {
      fetchTrackingRecordSave({
        uid: loginCtx?.userInfo?.uid,
        product_id: product.product_id,
        ...eventConfig,
        event_id: TrackingEventID.NewShowcaseAuthorize,
        event_type: TrackingEventType.Click,
        btn_id: TrackingBtnId.ShowcaseAuthorize,
      });

      fetchShowcaseAuthUrl(
        {
          callback,
          product_id: product.hash_id,
          source: base64Encode(callback),
          ...(eventConfig?.module_id && { module_id: eventConfig?.module_id }),
          ...(eventConfig?.btn_id && { btn_id: eventConfig?.btn_id }),
        },
        {
          onSuccess: (res) => {
            onCancel();
            clientWindow?.open(res?.data.url);
          },
          onError: onErrorMessage,
        }
      );
    }
  }, [
    callback,
    fetchTrackingRecordSave,
    loginCtx?.userInfo?.uid,
    product,
    eventConfig,
    fetchShowcaseAuthUrl,
    onCancel,
  ]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      autoFocus={false}
      focusLock={true}
      className={"yf-showcase-auth-modal"}
      footer={null}
      closeIcon={<Icon.Close width={30} height={30} />}
    >
      <div className="yf-showcase-auth-content">
        <Icon.Security className="security-icon" />
        <h2 className="yf-showcase-auth-title">
          Showcase Management Authorization
        </h2>
        <div className="yf-showcase-auth-icon-indication">
          <Icon.YooFindsSquare />
          <Icon.ThreeRight />
          <Icon.TikTokSquare />
        </div>
        <p className="yf-showcase-auth-tip tip1">
          Tap &apos;
          <strong className="yf-authorize-now">
            &quot;Authorize Now&quot;
          </strong>
          &apos; to enable showcase management via TikTok.
        </p>
        <p className="yf-showcase-auth-tip tip2">
          Follow the prompts to authorize easily.
        </p>
        <label className="yf-showcase-auth-our-promise">Benefits:</label>
        <ul className="yf-showcase-auth-our-promise-list">
          <li>
            Smart sample management – Curated top-performing products for faster
            sales.
          </li>
        </ul>
        <label className="yf-showcase-auth-our-promise">Our Commitment:</label>
        <ul className="yf-showcase-auth-our-promise-list">
          <li>Secure & certified authorization.</li>
          <li>Privacy protected.</li>
        </ul>
        <Button
          size={"large"}
          className={"yf-show-case-auth-btn"}
          loading={isShowcaseAuthUrlLoading}
          onClick={goAuth}
        >
          Authorize Now
        </Button>
      </div>
    </Modal>
  );
};

export default ShowcaseAuthModal;
